import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Grid, DialogContent } from "@mui/material"

import { useAppDispatch } from "redux/hooks"
import { withModalStep, withConsent } from "hocs/features"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import {
  closeDialog,
  updateDialogStep,
} from "redux/features/dialog/dialogSlice"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import Title from "components/Refinance/Title"
import Terms from "components/Refinance/Sign/Terms"
import Providers from "components/Refinance/Sign/Providers"
import SmartId from "components/Refinance/Sign/SmartId"
import MobileId from "components/Refinance/Sign/MobileId"
import IdCard from "components/Refinance/Sign/IdCard"
import {
  REFINANCE_CONSENT_SIGN_STEP_INITIAL,
  REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
  REFINANCE_CONSENT_SIGN_STEP_SMART_ID,
  REFINANCE_CONSENT_SIGN_STEP_MOBILE_ID,
  REFINANCE_CONSENT_SIGN_STEP_ID_CARD,
} from "config"
import { ConsentType } from "types"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  consent: ConsentType
  step: number
}

const RefinanceConsentModal = ({
  consent,
  step = REFINANCE_CONSENT_SIGN_STEP_INITIAL,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isBackHidden = step === REFINANCE_CONSENT_SIGN_STEP_INITIAL

  const handleClose = () => {
    dispatch(closeDialog())
    navigate("/")
  }

  const handleSignSuccess = () => {
    dispatch(
      showNotice({
        message: t("notice.refinance.sign.success"),
      }),
    )
    handleClose()
  }

  const handleSignFailure = () => {
    dispatch(
      updateDialogStep({ step: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS }),
    )

    dispatch(
      showAlert({
        message: t("notice.refinance.sign.failure"),
      }),
    )
  }

  const handleBack = () => {
    switch (step) {
      case REFINANCE_CONSENT_SIGN_STEP_INITIAL:
        return
      case REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS:
        dispatch(
          updateDialogStep({ step: REFINANCE_CONSENT_SIGN_STEP_INITIAL }),
        )
        return
      case REFINANCE_CONSENT_SIGN_STEP_SMART_ID:
        dispatch(
          updateDialogStep({
            step: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
          }),
        )
        return
      case REFINANCE_CONSENT_SIGN_STEP_MOBILE_ID:
        dispatch(
          updateDialogStep({
            step: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
          }),
        )
        return
      case REFINANCE_CONSENT_SIGN_STEP_ID_CARD:
        dispatch(
          updateDialogStep({
            step: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
          }),
        )
        return
      default:
        return
    }
  }

  const STEPS = [
    {
      label: REFINANCE_CONSENT_SIGN_STEP_INITIAL,
      title: t("features.refinance.form.title"),
      component: (
        <Terms
          onSign={() =>
            dispatch(
              updateDialogStep({
                step: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
              }),
            )
          }
        />
      ),
    },
    {
      label: REFINANCE_CONSENT_SIGN_STEP_CHOOSE_PROVIDERS,
      title: t("features.refinance.sign.title"),
      component: (
        <Providers onChoose={(step) => dispatch(updateDialogStep({ step }))} />
      ),
    },
    {
      label: REFINANCE_CONSENT_SIGN_STEP_SMART_ID,
      title: t("features.refinance.sign.title"),
      component: (
        <SmartId
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: REFINANCE_CONSENT_SIGN_STEP_MOBILE_ID,
      title: t("features.refinance.sign.title"),
      component: (
        <MobileId
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: REFINANCE_CONSENT_SIGN_STEP_ID_CARD,
      component: (
        <IdCard
          consentId={consent.id}
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
  ]

  const renderTitle = () => {
    const item = STEPS.find(({ label }) => label === step)

    return <Title>{item?.title}</Title>
  }

  return (
    <DialogContent>
      <Back hidden={isBackHidden} onClick={handleBack} />
      <Close onClick={handleClose} />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {renderTitle()}
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withConsent,
)(RefinanceConsentModal)
